import React from "react";
import { useQuery } from "@tanstack/react-query";
import { formatDistance } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";

interface SupplierActivityProps {
  supplierId: string;
}

const SupplierActivity: React.FC<SupplierActivityProps> = ({ supplierId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ["supplier-activity", supplierId],
    enabled: !!supplierId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("activity_logs")
        .select("id, action, entity_type, created_at")
        .in("entity_type", ["supplier", "supplier_note", "supplier_line_item"])
        .eq("entity_id", supplierId)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      return data ?? [];
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-muted-foreground border border-dashed rounded-md">
        No activity yet for this supplier.
      </div>
    );
  }

  return (
    <ul className="divide-y">
      {data.map((entry) => (
        <li key={entry.id} className="flex items-center justify-between py-2.5 text-sm">
          <span>{entry.action}</span>
          <span className="text-xs text-muted-foreground">
            {formatDistance(new Date(entry.created_at), new Date(), { addSuffix: true })}
          </span>
        </li>
      ))}
    </ul>
  );
};

export default SupplierActivity;
